import { parseISO, isValid } from "date-fns";

import { Residence } from "./address-utils";

export const NO_UNDECLARED_RESIDENCE_OVERLAPS_KEYWORD =
  "noUndeclaredResidenceOverlaps";

function parseResidenceDate(
  dateStr: string | null | undefined
): Date | undefined {
  if (!dateStr) {
    return undefined;
  }

  const parsed = parseISO(dateStr);
  if (!isValid(parsed)) {
    return undefined;
  }
  parsed.setHours(0, 0, 0, 0);
  return parsed;
}

function utcMidnightMs(date: Date): number {
  return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
}

function residenceOverlapBounds(
  residenceA: Residence,
  residenceB: Residence
): { overlapStart: Date; overlapEnd: Date } | undefined {
  const startA = parseResidenceDate(residenceA.start_date);
  const startB = parseResidenceDate(residenceB.start_date);
  if (!startA || !startB) {
    return undefined;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const endA = parseResidenceDate(residenceA.end_date) ?? today;
  const endB = parseResidenceDate(residenceB.end_date) ?? today;
  const overlapStart = startA > startB ? startA : startB;
  const overlapEnd = endA < endB ? endA : endB;
  if (overlapStart > overlapEnd) {
    return undefined;
  }

  return { overlapStart, overlapEnd };
}

export function residencePeriodOverlapDays(
  residenceA: Residence,
  residenceB: Residence
): number {
  const bounds = residenceOverlapBounds(residenceA, residenceB);
  if (!bounds) {
    return 0;
  }

  const msPerDay = 24 * 60 * 60 * 1000;
  return (
    Math.floor(
      (utcMidnightMs(bounds.overlapEnd) - utcMidnightMs(bounds.overlapStart)) /
        msPerDay
    ) + 1
  );
}

/** True when overlap is longer than one day and spans multiple calendar months. */
export function residenceOverlapRequiresReview(
  residenceA: Residence,
  residenceB: Residence
): boolean {
  const overlapDays = residencePeriodOverlapDays(residenceA, residenceB);
  if (overlapDays <= 1) {
    return false;
  }

  const bounds = residenceOverlapBounds(residenceA, residenceB);
  if (!bounds) {
    return false;
  }

  const sameCalendarMonth =
    bounds.overlapStart.getFullYear() === bounds.overlapEnd.getFullYear() &&
    bounds.overlapStart.getMonth() === bounds.overlapEnd.getMonth();
  return !sameCalendarMonth;
}

function normalizeAddressToken(value: string | undefined): string {
  return (value ?? "").trim().toLowerCase();
}

function overlapDedupExemptFromReview(
  residenceA: Residence,
  residenceB: Residence
): boolean {
  const addrA = residenceA.address ?? {};
  const addrB = residenceB.address ?? {};

  if (
    normalizeAddressToken(addrA.locality) !==
      normalizeAddressToken(addrB.locality) ||
    normalizeAddressToken(addrA.administrative_division_1) !==
      normalizeAddressToken(addrB.administrative_division_1) ||
    normalizeAddressToken(addrA.country) !==
      normalizeAddressToken(addrB.country)
  ) {
    return false;
  }

  const streetA = normalizeAddressToken(addrA.street_address);
  const streetB = normalizeAddressToken(addrB.street_address);
  if (!streetA || !streetB || streetA !== streetB) {
    return false;
  }

  const postalCodeA = (addrA.postal_code ?? "").trim();
  const postalCodeB = (addrB.postal_code ?? "").trim();
  return Boolean(postalCodeA && postalCodeB);
}

/** Mirrors backend ``residences_have_undeclared_overlap`` for UI rules and AJV. */
export function residencesHaveUndeclaredOverlap(
  residences: Residence[] | null | undefined
): boolean {
  if (!residences?.length) {
    return false;
  }

  for (let index = 0; index < residences.length; index += 1) {
    const earlier = residences[index];
    for (
      let laterIndex = index + 1;
      laterIndex < residences.length;
      laterIndex += 1
    ) {
      const later = residences[laterIndex];
      if (!residenceOverlapRequiresReview(earlier, later)) {
        continue;
      }
      if (overlapDedupExemptFromReview(earlier, later)) {
        continue;
      }
      return true;
    }
  }

  return false;
}
